// src/components/block/liveStreamManagment/AutoDirectorSettingsModal.tsx
// v1.0 — Réglages du Réalisateur auto.
// v1.1 (2026-10-24) — présentation alignée sur « Config pupitre » (PupitreConfigModal,
//   charte de référence des fenêtres de la régie) : voile flouté, fond de panneau,
//   accent indigo, textes 12–16 px, réglages regroupés en cartes. Logique inchangée.
//
// Validation différée, comme SourceAliasConfigModal : rien n'est appliqué avant
// « Valider », « Annuler » ou la croix ferment sans toucher aux réglages en cours.
//
// Chaque source candidate affiche son niveau en direct avec le seuil de silence
// tracé dessus : c'est la façon de régler ce seuil sans deviner ce que « 35 »
// veut dire — on parle dans le micro et on voit la barre passer la ligne.

import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Clapperboard, X } from 'lucide-react';

import { useAudioLevelEffect } from '../../../hooks/liveStreamManagment/useAudioLevel';
import {
  AUTO_DIRECTOR_LIMITS, AUTO_DIRECTOR_MODES,
  AutoDirectorMode, AutoDirectorSettings,
} from './autoDirector';

export interface AutoDirectorCandidate {
  id    : string;
  label : string;
}

interface Props {
  open       : boolean;
  onClose    : () => void;
  settings   : AutoDirectorSettings;
  onApply    : (next: AutoDirectorSettings) => void;
  candidates : AutoDirectorCandidate[];
}

// ── Charte (mêmes valeurs que PupitreConfigModal) ─────────────────────────────

const C = {
  text:    '#f1f5f9',
  soft:    '#cbd5e1',
  muted:   '#94a3b8',
  faint:   '#64748b',
  line:    'rgba(255,255,255,0.08)',
  accent:  '#818cf8',
  accentBg:'rgba(99,102,241,0.16)',
  primary: '#6366f1',
};

const LABEL: React.CSSProperties = { fontSize: 13, color: C.text, fontWeight: 600 };
const HINT : React.CSSProperties = { fontSize: 12, color: C.muted, lineHeight: 1.5 };

const CARD: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 10,
  padding: 14, borderRadius: 12,
  background: 'rgba(255,255,255,0.03)', border: `1px solid ${C.line}`,
};
const CARD_TITLE: React.CSSProperties = {
  fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.06em',
};

const BTN_BASE: React.CSSProperties = {
  height: 34, padding: '0 18px', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer',
  display: 'inline-flex', alignItems: 'center', gap: 6,
};
const BTN_GHOST:   React.CSSProperties = { ...BTN_BASE, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.14)', color: C.soft };
const BTN_PRIMARY: React.CSSProperties = { ...BTN_BASE, background: C.primary, border: '1px solid #818cf8', color: '#fff', boxShadow: '0 4px 14px rgba(99,102,241,0.35)' };

/** Barre de niveau en direct, avec le seuil de silence en repère. */
const LiveLevel: React.FC<{ sourceId: string; thresholdPct: number }> = ({ sourceId, thresholdPct }) => {
  const barRef = useRef<HTMLDivElement>(null);
  useAudioLevelEffect(sourceId, ({ avg }) => {
    const bar = barRef.current;
    if (!bar) return;
    bar.style.width = `${avg}%`;
    bar.style.background = avg >= thresholdPct ? '#22c55e' : '#64748b';
  });
  return (
    <div style={{ position: 'relative', flex: 1, minWidth: 0, height: 8, background: 'rgba(0,0,0,0.35)', border: `1px solid ${C.line}`, borderRadius: 4, overflow: 'hidden' }}>
      <div ref={barRef} style={{ height: '100%', width: '0%', background: '#64748b', transition: 'width 40ms' }} />
      <div title="Seuil de silence" style={{ position: 'absolute', top: -1, bottom: -1, left: `${thresholdPct}%`, width: 2, background: '#f59e0b', boxShadow: '0 0 4px rgba(245,158,11,0.8)' }} />
    </div>
  );
};

const NumberField: React.FC<{
  label: string; hint: string; unit: string;
  value: number; min: number; max: number; step: number;
  onChange: (v: number) => void;
}> = ({ label, hint, unit, value, min, max, step, onChange }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
      <span style={LABEL}>{label}</span>
      <span style={{
        fontSize: 12, fontWeight: 700, color: '#e0e7ff', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums',
        padding: '2px 8px', borderRadius: 6, background: C.accentBg, border: '1px solid rgba(129,140,248,0.4)',
      }}>
        {value}{unit}
      </span>
    </div>
    <input
      type="range" min={min} max={max} step={step} value={value}
      onChange={e => onChange(Number(e.target.value))}
      style={{ width: '100%', accentColor: C.primary }}
    />
    <span style={HINT}>{hint}</span>
  </div>
);

const AutoDirectorSettingsModal: React.FC<Props> = ({ open, onClose, settings, onApply, candidates }) => {
  const [local, setLocal] = useState<AutoDirectorSettings>(settings);

  // À chaque ouverture on repart des réglages en vigueur, pas d'un brouillon abandonné.
  useEffect(() => { if (open) setLocal(settings); }, [open, settings]);

  if (!open) return null;

  const L = AUTO_DIRECTOR_LIMITS;
  const patch = (p: Partial<AutoDirectorSettings>) => setLocal(prev => ({ ...prev, ...p }));

  // null = « toutes » : on ne le fige en liste qu'au premier décochage.
  const allIds     = candidates.map(c => c.id);
  const selected   = local.candidateIds ?? allIds;
  const isSelected = (id: string) => selected.includes(id);
  const toggleCandidate = (id: string) => {
    const next = isSelected(id) ? selected.filter(x => x !== id) : [...selected, id];
    patch({ candidateIds: next.length === allIds.length ? null : next });
  };

  const apply = () => { onApply(local); onClose(); };

  const admitted = candidates.filter(c => isSelected(c.id)).length;

  return ReactDOM.createPortal(
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(8px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}>
      <style>{`
        .adm-select option { background: #1a2035 !important; color: #e2e8f0 !important; }
        .adm-select:focus { border-color: #818cf8 !important; box-shadow: 0 0 0 3px rgba(99,102,241,0.25); }
        .adm-row:hover { background: rgba(255,255,255,0.035) !important; }
      `}</style>
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'var(--color-Panel-niv1-BG)', border: `1px solid ${C.line}`, borderRadius: 16,
          width: 600, maxWidth: '94vw', maxHeight: '88vh',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          boxShadow: '0 32px 80px rgba(0,0,0,0.85)',
        }}
      >
        {/* ── En-tête ── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 20px', borderBottom: `1px solid ${C.line}`, flexShrink: 0 }}>
          <span style={{ width: 32, height: 32, borderRadius: 9, background: C.accentBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Clapperboard style={{ width: 16, height: 16, color: C.accent }} />
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: C.text }}>Réalisateur auto</div>
            <div style={{ fontSize: 12, color: C.muted }}>Choisit tout seul la source à mettre à l'antenne</div>
          </div>
          <button onClick={onClose} title="Fermer sans appliquer"
            style={{ background: 'rgba(255,255,255,0.06)', border: `1px solid ${C.line}`, color: C.soft, width: 32, height: 32, borderRadius: 9, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <X style={{ width: 16, height: 16 }} />
          </button>
        </div>

        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14, overflowY: 'auto' }}>

          {/* Mécanisme de choix */}
          <div style={CARD}>
            <span style={CARD_TITLE}>Mécanisme de choix</span>
            <select
              className="adm-select"
              value={local.mode}
              onChange={e => patch({ mode: e.target.value as AutoDirectorMode })}
              style={{ height: 36, padding: '0 10px', borderRadius: 8, background: '#0b1222', border: '1px solid rgba(129,140,248,0.4)', color: C.text, fontSize: 13, colorScheme: 'dark', outline: 'none' }}
            >
              {AUTO_DIRECTOR_MODES.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
            </select>
            <span style={HINT}>{AUTO_DIRECTOR_MODES.find(m => m.id === local.mode)?.description}</span>
          </div>

          {/* Sources admises, avec niveau en direct */}
          <div style={CARD}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={CARD_TITLE}>Sources admises</span>
              <span style={{ flex: 1 }} />
              {candidates.length > 0 && (
                <span style={{ fontSize: 11, fontWeight: 600, color: C.muted, fontVariantNumeric: 'tabular-nums' }}>
                  {admitted} / {candidates.length}
                </span>
              )}
            </div>
            {candidates.length === 0 ? (
              <span style={{ ...HINT, fontStyle: 'italic' }}>Aucune source dans le deck.</span>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {candidates.map(c => (
                  <label key={c.id} className="adm-row"
                    style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', padding: '6px 8px', borderRadius: 8, opacity: isSelected(c.id) ? 1 : 0.55 }}>
                    <input type="checkbox" checked={isSelected(c.id)} onChange={() => toggleCandidate(c.id)}
                      style={{ accentColor: C.primary, width: 16, height: 16, flexShrink: 0 }} />
                    <span style={{ fontSize: 13, color: C.text, width: 180, flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={c.label}>{c.label}</span>
                    <LiveLevel sourceId={c.id} thresholdPct={local.silenceThresholdPct} />
                  </label>
                ))}
              </div>
            )}
            <span style={{ ...HINT, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <span style={{ width: 10, height: 10, borderRadius: 2, background: '#f59e0b', display: 'inline-block' }} />
              La ligne orange est le seuil de silence : une source dont la barre reste en dessous ne prend jamais l'antenne.
            </span>
          </div>

          {/* Réglages fins */}
          <div style={{ ...CARD, gap: 16 }}>
            <span style={CARD_TITLE}>Réglages</span>
            {local.mode === 'loudest' && (
              <>
                <NumberField
                  label="Seuil de silence" unit=" %" {...L.silenceThresholdPct}
                  value={local.silenceThresholdPct} onChange={v => patch({ silenceThresholdPct: v })}
                  hint="Échelle des VU-mètres. Plus haut = il faut parler plus fort pour prendre l'antenne."
                />
                <NumberField
                  label="Avance requise sur la source à l'antenne" unit=" pts" {...L.switchMarginPct}
                  value={local.switchMarginPct} onChange={v => patch({ switchMarginPct: v })}
                  hint="Évite d'alterner entre deux personnes qui parlent à peu près aussi fort."
                />
                <NumberField
                  label="Délai de réaction" unit=" ms" {...L.reactionMs}
                  value={local.reactionMs} onChange={v => patch({ reactionMs: v })}
                  hint="Durée pendant laquelle la nouvelle source doit rester la plus forte avant la bascule."
                />
                <NumberField
                  label="Durée minimale à l'antenne" unit=" ms" {...L.minHoldMs}
                  value={local.minHoldMs} onChange={v => patch({ minHoldMs: v })}
                  hint="Une source qui vient de passer en final n'est pas remplacée avant ce délai."
                />
              </>
            )}

            <NumberField
              label="Pause après un choix manuel" unit=" ms" {...L.manualPauseMs}
              value={local.manualPauseMs} onChange={v => patch({ manualPauseMs: v })}
              hint="Quand vous cliquez vous-même une source, le réalisateur se tait ce temps-là. 0 = il reprend aussitôt."
            />
          </div>
        </div>

        {/* ── Pied ── */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10, padding: '12px 20px', borderTop: `1px solid ${C.line}`, flexShrink: 0 }}>
          <span style={{ marginRight: 'auto', fontSize: 12, color: C.faint }}>Rien n'est appliqué avant « Valider »</span>
          <button onClick={onClose} style={BTN_GHOST}>Annuler</button>
          <button onClick={apply} style={BTN_PRIMARY}>Valider</button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default AutoDirectorSettingsModal;
